import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { updateSignupField, resetSignupForm } from "./redux/signupSlice";
import { loginUser } from "./redux/signupThunk";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "./Login.css";
import userStore from "../../../zustand/userStore";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const fieldVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 100, damping: 15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 80, damping: 20, duration: 0.6 },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 40,
    transition: { duration: 0.3 },
  },
};

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formData, loader, error } = useSelector((state) => state.signup);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const setUser = userStore.getState().setUser;

  const validate = () => {
    const errors = {};
    if (!formData.email.trim()) {
      errors.email = "Mobile or Email is required";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    return errors;
  };

  const handleChange = (field, value) => {
    dispatch(updateSignupField({ field, value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    dispatch(loginUser({ email: formData.email, password: formData.password }))
      .unwrap()
      .then((data) => {
        // 🔴 Verification pending
        console.log("data?.message", data?.message);
        if (data?.message === "VERIFICATION_PENDING") {
          // toast.warning(
          //   data.warning || "Please verify your account to continue",
          // );
          let toastMessage = "";

          if (
            !data?.verification?.email_verified &&
            !data?.verification?.mobile_verified
          ) {
            toastMessage =
              "Please verify your email and mobile number to continue";
          } else if (!data?.verification?.email_verified) {
            toastMessage = "Please verify your email to continue";
          } else if (!data?.verification?.mobile_verified) {
            toastMessage = "Please verify your mobile number to continue";
          }

          if (toastMessage) {
            alert(toastMessage);
            // toast(toastMessage, {
            //   icon: "⚠️",
            // });
          }

          let emailValue = "";
          let mobileValue = "";

          if (!data?.verification?.email_verified) {
            emailValue = data?.data?.email;
          }

          if (!data?.verification?.mobile_verified) {
            mobileValue = data?.data?.contact_number;
          }

          navigate("/otp", {
            state: {
              formData: {
                email: emailValue,
                contact_number: mobileValue,
                password: formData?.password,
                email_id: data?.data?.email,
              },
              source: "login",
            },
          });

          return;
        }

        // 🟢 Verified user
        toast.success(data.message || "Login successful");
        navigate("/");
      })
      .catch((err) => {
        toast.error(
          err?.message || err?.response?.data?.message || "Login failed",
        );
      });
    // dispatch(loginUser({ email: formData.email, password: formData.password }))
    //   .unwrap()
    //   .then((data) => {
    //     toast.success(data.message || "Login successful");

    //     console.log("data", JSON.stringify(data?.data));
    //     const datToSet = {
    //       ...data?.data,
    //       isMobile: 0,
    //     };
    //     setUser(datToSet);
    //     if (datToSet?.isMobile === 0) {
    //       navigate("/otp", {
    //         state: { formData: data?.data },
    //       });
    //     } else {
    //       navigate("/");
    //     }
    //     // setUser(data.data)
    //   })
    //   .catch((err) => {
    //     toast.error(err || "Login failed");
    //   });
  };

  const handleBackToHome = () => {
    dispatch(resetSignupForm());
    navigate("/");
  };

  return (
    <div className="login-page">
      <Toaster position="top-right" />
      {/* Animated background blobs */}

      <motion.div
        className="login-blob login-blob-1"
        animate={{
          x: [0, 30, -20, 0],
          y: [0, -40, 20, 0],
          scale: [1, 1.1, 0.95, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="login-blob login-blob-2"
        animate={{
          x: [0, -40, 25, 0],
          y: [0, 30, -30, 0],
          scale: [1, 0.95, 1.1, 1],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="login-blob login-blob-3"
        animate={{
          x: [0, 20, -30, 0],
          y: [0, -20, 40, 0],
          scale: [1, 1.05, 0.9, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key="form"
          className="login-card"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Back button */}
          <motion.button
            className="login-back-btn"
            onClick={handleBackToHome}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowLeft size={20} />
          </motion.button>

          <div className="login-card-header">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Welcome Back
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Sign in to your account
            </motion.p>
          </div>

          <motion.form
            className="login-form"
            onSubmit={handleSubmit}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Email Address */}
            <motion.div
              className={`login-field ${validationErrors.email ? "login-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="loginEmail">Mobile or Email</label>
              <div className="login-input-wrapper">
                <input
                  id="loginEmail"
                  type="text"
                  placeholder="Enter mobile or email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  autoComplete="email"
                />
              </div>
              <AnimatePresence>
                {validationErrors.email && (
                  <motion.span
                    className="login-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.email}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Password */}
            <motion.div
              className={`login-field ${validationErrors.password ? "login-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="loginPassword">Password</label>
              <div className="login-input-wrapper">
                <input
                  id="loginPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowPassword((p) => !p)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <AnimatePresence>
                {validationErrors.password && (
                  <motion.span
                    className="login-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.password}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {error && (
              <motion.div
                className="login-api-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {error}
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="login-submit-btn"
              disabled={loader}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 8px 30px rgba(99, 102, 241, 0.4)",
              }}
              whileTap={{ scale: 0.98 }}
              variants={fieldVariants}
            >
              {loader ? (
                <span className="login-spinner-wrap">
                  <motion.span
                    className="login-spinner"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  Signing In...
                </span>
              ) : (
                "Login"
              )}
            </motion.button>
          </motion.form>

          <motion.p
            className="login-footer-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Don't have an account?{" "}
            <span
              className="login-signup-link"
              onClick={() => {
                dispatch(resetSignupForm());
                navigate("/signup");
              }}
            >
              Sign Up
            </span>
          </motion.p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
